/**
 * The answer-grounding badge's recovery actions (MAR-206): "Revisar respuesta" and "Buscar fuentes
 * de respaldo". Composed by the `web-chat` store facade, which passes its refs in as `deps`.
 *
 * THE BROWSER SENDS WHAT TO RECOVER, NEVER HOW. Each action is one hidden turn whose text is
 * `GROUNDING_RECOVERY_SENTINEL` and whose `recovery` field is the structured request
 * (`shared/chat/groundingRecovery.ts`). The wording is the web-chat coordinator's
 * (`services/agent-web-chat/src/marcus_web_chat/recovery.py`, services/agent-web-chat/AGENTS.md); a browser-side prompt
 * once named a tool that did not exist (#890). The recovered answer is validated like any other
 * turn, which is the re-validation after a revision.
 *
 * THE ANSWER IS SAVED, THE HIDDEN TURN IS NOT (`persist: 'answer'`, Alex 2026-09-25). The revision
 * becomes a new assistant message after the original, so a reload keeps both. The sentinel user turn
 * stays out of `conversation_messages`: that table has no "hidden" flag, so a saved sentinel would
 * come back as a visible message the user never wrote, and the edit flow could branch from it.
 * Leaving it out keeps history correct: editing the user's real question still truncates the
 * original AND the revision after it, since both come later. The ADK session keeps the sentinel turn
 * server-side, where `recovery.py` leaves it inert.
 */
import type { Ref } from 'vue'
import type { WebChatMessage } from '~/types/web-chat'
import type { ApiFetch } from '~/composables/useApi'
import { normalizeValidationResult } from '~/composables/web-chat/normalizers'
import {
  GROUNDING_RECOVERY_SENTINEL,
  type GroundingRecoveryAction,
  type GroundingRecoveryClaim,
  type GroundingRecoveryRequest,
  type GroundingRevalidateRequest,
  type GroundingRevalidateResponse,
} from '~~/shared/chat/groundingRecovery'

export interface GroundingRecoveryDeps {
  messages: Ref<WebChatMessage[]>
  isStreaming: Ref<boolean>
  sendMessage: (
    content: string,
    attachments: undefined,
    options: { hiddenFromUi: boolean; persist: 'answer'; recovery: GroundingRecoveryRequest },
  ) => Promise<unknown>
  apiFetch: ApiFetch
}

/** The claims the badge shows as flagged, in the shape a recovery request carries. */
function flaggedClaimsOf(message: WebChatMessage): GroundingRecoveryClaim[] {
  return ((message.validation?.failedClaims ?? message.validation?.claims) ?? [])
    .filter(claim => claim.verdict !== 'supported' && claim.verdict !== 'not_legal_claim')
    .map(claim => ({
      claim: claim.claim,
      verdict: claim.verdict,
      reason: claim.reason,
      severity: claim.severity,
      ...(claim.citationLabels?.length ? { citationLabels: claim.citationLabels } : {}),
    }))
}

export function useGroundingRecovery(deps: GroundingRecoveryDeps) {
  function assistantMessage(messageId: string): WebChatMessage | undefined {
    if (deps.isStreaming.value) return undefined
    return deps.messages.value.find(m => m.id === messageId && m.role === 'assistant')
  }

  function sendRecovery(message: WebChatMessage, action: GroundingRecoveryAction, flaggedClaims: GroundingRecoveryClaim[]) {
    return deps.sendMessage(GROUNDING_RECOVERY_SENTINEL, undefined, {
      hiddenFromUi: true,
      // The revision is kept as a new assistant message after the original; the hidden turn is not.
      persist: 'answer',
      recovery: { action, messageId: message.id, previousAnswer: message.content, flaggedClaims },
    })
  }

  async function reviseUnsupportedStatements(messageId: string) {
    const message = assistantMessage(messageId)
    if (!message) return
    const flaggedClaims = flaggedClaimsOf(message)
    if (flaggedClaims.length) await sendRecovery(message, 'revise', flaggedClaims)
  }

  /**
   * Re-validate first, then search only for what is still flagged. The re-validation is a judge
   * call, not a chat turn: it settles an answer whose first check was `unavailable`, or was taken
   * before an evidence fix, with nothing rewritten.
   */
  async function findSupportingSources(messageId: string) {
    const message = assistantMessage(messageId)
    if (!message) return

    const previous = message.validation
    message.validation = { status: 'running' }
    try {
      const response = await deps.apiFetch<GroundingRevalidateResponse>('/api/chat/revalidate', {
        method: 'POST',
        body: { answerText: message.content, messageId: message.id } satisfies GroundingRevalidateRequest,
      })
      message.validation = normalizeValidationResult(response.result)
    } catch (err) {
      console.error('Failed to re-validate the answer:', err)
      message.validation = previous
      return
    }

    const flaggedClaims = flaggedClaimsOf(message)
    if (message.validation?.status === 'warning' && flaggedClaims.length) {
      await sendRecovery(message, 'find_supporting', flaggedClaims)
    }
  }

  /** The badge's single event: which recovery the user picked, for which answer. */
  function recoverGrounding(messageId: string, action: GroundingRecoveryAction) {
    return action === 'find_supporting' ? findSupportingSources(messageId) : reviseUnsupportedStatements(messageId)
  }

  return { reviseUnsupportedStatements, findSupportingSources, recoverGrounding }
}
